import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  Chip,
  Button,
  Collapse,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Autocomplete,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface MedicalRecord {
  id: number;
  pet_id: number;
  pet_name: string;
  owner_name?: string;
  veterinarian_name?: string;
  visit_date: string;
  status: string;
  complaint?: string;
  symptoms?: string;
  diagnosis?: string;
  physical_exam_findings?: string;
  treatment_plan?: string;
  prognosis?: string;
  follow_up_date?: string;
  follow_up_instructions?: string;
  referral_to?: string;
  referral_notes?: string;
  notes?: string;
}

interface PetOption { id: string; label: string; }

const STATUS_COLORS: Record<string, 'warning' | 'success' | 'error' | 'default' | 'info'> = {
  draft: 'warning',
  completed: 'success',
  locked: 'error',
};

const emptyForm = {
  visit_date: new Date().toISOString().split('T')[0],
  status: 'draft',
  complaint: '',
  symptoms: '',
  diagnosis: '',
  physical_exam_findings: '',
  treatment_plan: '',
  prognosis: '',
  follow_up_date: '',
  follow_up_instructions: '',
  referral_to: '',
  referral_notes: '',
  notes: '',
};

// ── Expandable row ────────────────────────────────────────────────────────────

const ExpandableRow = ({
  record,
  onEdit,
}: {
  record: MedicalRecord;
  onEdit: (r: MedicalRecord) => void;
}) => {
  const [open, setOpen] = useState(false);

  const hasFollowUp = record.follow_up_date || record.follow_up_instructions;
  const hasReferral = record.referral_to || record.referral_notes;

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{record.pet_name}</TableCell>
        <TableCell>{record.owner_name || '—'}</TableCell>
        <TableCell>{record.veterinarian_name || '—'}</TableCell>
        <TableCell>{record.visit_date}</TableCell>
        <TableCell>{record.diagnosis || '—'}</TableCell>
        <TableCell>
          <Chip
            label={record.status}
            color={STATUS_COLORS[record.status] ?? 'default'}
            size="small"
          />
        </TableCell>
        <TableCell align="center">
          {record.status === 'locked' ? (
            <Tooltip title="Record is locked">
              <span>
                <IconButton size="small" disabled>
                  <LockIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title="Edit record">
              <IconButton size="small" onClick={() => onEdit(record)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 3 }}>
              <Grid container spacing={2}>
                {record.complaint && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>Chief Complaint</Typography>
                    <Typography variant="body2">{record.complaint}</Typography>
                  </Grid>
                )}
                {record.symptoms && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>Symptoms</Typography>
                    <Typography variant="body2">{record.symptoms}</Typography>
                  </Grid>
                )}
                {record.physical_exam_findings && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Physical Exam Findings</Typography>
                    <Typography variant="body2">{record.physical_exam_findings}</Typography>
                  </Grid>
                )}
                {record.diagnosis && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>Diagnosis</Typography>
                    <Typography variant="body2">{record.diagnosis}</Typography>
                  </Grid>
                )}
                {record.prognosis && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>Prognosis</Typography>
                    <Typography variant="body2">{record.prognosis}</Typography>
                  </Grid>
                )}
                {record.treatment_plan && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Treatment Plan</Typography>
                    <Typography variant="body2">{record.treatment_plan}</Typography>
                  </Grid>
                )}
                {record.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Notes</Typography>
                    <Typography variant="body2">{record.notes}</Typography>
                  </Grid>
                )}
              </Grid>

              {(hasFollowUp || hasReferral) && <Divider sx={{ my: 2 }} />}

              {hasFollowUp && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>Follow-up</Typography>
                  <Grid container spacing={2}>
                    {record.follow_up_date && (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="textSecondary">Date</Typography>
                        <Typography variant="body2">{record.follow_up_date}</Typography>
                      </Grid>
                    )}
                    {record.follow_up_instructions && (
                      <Grid item xs={12} sm={8}>
                        <Typography variant="body2" color="textSecondary">Instructions</Typography>
                        <Typography variant="body2">{record.follow_up_instructions}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {hasReferral && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Referral</Typography>
                  <Grid container spacing={2}>
                    {record.referral_to && (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="textSecondary">Referred To</Typography>
                        <Typography variant="body2">{record.referral_to}</Typography>
                      </Grid>
                    )}
                    {record.referral_notes && (
                      <Grid item xs={12} sm={8}>
                        <Typography variant="body2" color="textSecondary">Referral Notes</Typography>
                        <Typography variant="body2">{record.referral_notes}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {!record.complaint && !record.symptoms && !record.diagnosis && !record.treatment_plan && (
                <Typography variant="body2" color="textSecondary">No clinical details recorded.</Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const MedicalRecordsPage = () => {
  const [records, setRecords] = useState<MedicalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pageError, setPageError] = useState<string | null>(null);

  // Create / Edit dialog shared state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MedicalRecord | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [selectedPet, setSelectedPet] = useState<PetOption | null>(null);
  const [petOptions, setPetOptions] = useState<PetOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  useEffect(() => { fetchRecords(); }, []);

  const fetchRecords = async () => {
    setLoading(true);
    setPageError(null);
    try {
      const params: Record<string, string> = {};
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const response = await api.get(API_ENDPOINTS.MEDICAL_RECORDS.LIST, { params });
      const raw = response.data ?? response ?? [];
      setRecords(Array.isArray(raw) ? raw : []);
    } catch {
      setPageError('Failed to load medical records.');
    } finally {
      setLoading(false);
    }
  };

  const loadPetOptions = async () => {
    try {
      const res = await api.get(API_ENDPOINTS.PATIENTS.LIST);
      const pets = res.data ?? res ?? [];
      setPetOptions(
        (pets as { id: string; name: string; species?: string }[]).map(p => ({
          id: String(p.id),
          label: p.species ? `${p.name} (${p.species})` : p.name,
        }))
      );
    } catch {
      setPetOptions([]);
    }
  };

  // ── Open create dialog ─────────────────────────────────────────────────────
  const openCreate = async () => {
    setEditingRecord(null);
    setForm({ ...emptyForm, visit_date: new Date().toISOString().split('T')[0] });
    setSelectedPet(null);
    setDialogError(null);
    await loadPetOptions();
    setDialogOpen(true);
  };

  // ── Open edit dialog ───────────────────────────────────────────────────────
  const openEdit = async (record: MedicalRecord) => {
    setEditingRecord(record);
    setForm({
      visit_date: record.visit_date ?? '',
      status: record.status ?? 'draft',
      complaint: record.complaint ?? '',
      symptoms: record.symptoms ?? '',
      diagnosis: record.diagnosis ?? '',
      physical_exam_findings: record.physical_exam_findings ?? '',
      treatment_plan: record.treatment_plan ?? '',
      prognosis: record.prognosis ?? '',
      follow_up_date: record.follow_up_date ?? '',
      follow_up_instructions: record.follow_up_instructions ?? '',
      referral_to: record.referral_to ?? '',
      referral_notes: record.referral_notes ?? '',
      notes: record.notes ?? '',
    });
    setSelectedPet({ id: String(record.pet_id), label: record.pet_name });
    setDialogError(null);
    await loadPetOptions();
    setDialogOpen(true);
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!editingRecord && !selectedPet) { setDialogError('Patient is required'); return; }
    if (!form.visit_date) { setDialogError('Visit date is required'); return; }

    setSaving(true);
    setDialogError(null);

    const payload: Record<string, unknown> = {
      visit_date: form.visit_date,
      status: form.status,
      complaint: form.complaint || undefined,
      symptoms: form.symptoms || undefined,
      diagnosis: form.diagnosis || undefined,
      physical_exam_findings: form.physical_exam_findings || undefined,
      treatment_plan: form.treatment_plan || undefined,
      prognosis: form.prognosis || undefined,
      follow_up_date: form.follow_up_date || undefined,
      follow_up_instructions: form.follow_up_instructions || undefined,
      referral_to: form.referral_to || undefined,
      referral_notes: form.referral_notes || undefined,
      notes: form.notes || undefined,
    };

    try {
      if (editingRecord) {
        await api.put(API_ENDPOINTS.MEDICAL_RECORDS.UPDATE(String(editingRecord.id)), payload);
      } else {
        await api.post(API_ENDPOINTS.MEDICAL_RECORDS.CREATE, {
          ...payload,
          pet_id: Number(selectedPet!.id),
        });
      }
      setDialogOpen(false);
      fetchRecords();
    } catch {
      setDialogError('Failed to save record. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const filtered = records.filter(r =>
    `${r.pet_name} ${r.owner_name ?? ''} ${r.diagnosis ?? ''}`.toLowerCase().includes(search.toLowerCase())
  );

  const f = (key: keyof typeof emptyForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [key]: e.target.value }));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Medical Records</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Record
        </Button>
      </Box>

      {pageError && <Alert severity="error" sx={{ mb: 2 }}>{pageError}</Alert>}

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <TextField
          label="Search"
          placeholder="Pet, owner, diagnosis..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <TextField
          label="From" type="date" InputLabelProps={{ shrink: true }}
          value={dateFrom} onChange={e => setDateFrom(e.target.value)}
        />
        <TextField
          label="To" type="date" InputLabelProps={{ shrink: true }}
          value={dateTo} onChange={e => setDateTo(e.target.value)}
        />
        <Button variant="outlined" onClick={fetchRecords}>Filter</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={50} />
              <TableCell>Pet</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Vet</TableCell>
              <TableCell>Visit Date</TableCell>
              <TableCell>Diagnosis</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">No medical records found</TableCell>
              </TableRow>
            ) : filtered.map(record => (
              <ExpandableRow key={record.id} record={record} onEdit={openEdit} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRecord ? 'Edit Medical Record' : 'New Medical Record'}</DialogTitle>
        <DialogContent>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Patient + date row */}
            <Grid item xs={12} sm={6}>
              {editingRecord ? (
                <TextField label="Patient" value={editingRecord.pet_name} disabled fullWidth />
              ) : (
                <Autocomplete
                  options={petOptions}
                  getOptionLabel={o => o.label}
                  value={selectedPet}
                  onChange={(_, val) => setSelectedPet(val)}
                  renderInput={params => <TextField {...params} label="Patient *" />}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Visit Date *" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={form.visit_date} onChange={f('visit_date')}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.status}
                  label="Status"
                  onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="locked">Locked</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider textAlign="left"><Typography variant="caption" color="textSecondary">Clinical Information</Typography></Divider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Chief Complaint" multiline rows={2} fullWidth
                value={form.complaint} onChange={f('complaint')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Symptoms" multiline rows={2} fullWidth
                value={form.symptoms} onChange={f('symptoms')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Physical Exam Findings" multiline rows={3} fullWidth
                value={form.physical_exam_findings} onChange={f('physical_exam_findings')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Diagnosis" multiline rows={2} fullWidth
                value={form.diagnosis} onChange={f('diagnosis')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Prognosis" multiline rows={2} fullWidth
                value={form.prognosis} onChange={f('prognosis')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Treatment Plan" multiline rows={3} fullWidth
                value={form.treatment_plan} onChange={f('treatment_plan')}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider textAlign="left"><Typography variant="caption" color="textSecondary">Follow-up</Typography></Divider>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Follow-up Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={form.follow_up_date} onChange={f('follow_up_date')}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Follow-up Instructions" multiline rows={2} fullWidth
                value={form.follow_up_instructions} onChange={f('follow_up_instructions')}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider textAlign="left"><Typography variant="caption" color="textSecondary">Referral</Typography></Divider>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Referred To" fullWidth
                value={form.referral_to} onChange={f('referral_to')}
                placeholder="e.g. Dr. Smith / City Animal Hospital"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Referral Notes" multiline rows={2} fullWidth
                value={form.referral_notes} onChange={f('referral_notes')}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider textAlign="left"><Typography variant="caption" color="textSecondary">Additional Notes</Typography></Divider>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Internal Notes" multiline rows={2} fullWidth
                value={form.notes} onChange={f('notes')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editingRecord ? 'Save Changes' : 'Create Record'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicalRecordsPage;
